import React from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from './SuperAdminAuth';
import {
  LayoutDashboard, Building2, CreditCard, Users, UsersRound, Mail, Flag,
  ScrollText, Activity, Megaphone, Shield, LogOut, Gauge, Rocket, Package, KeyRound, Wrench,
  ShieldCheck,
} from 'lucide-react';

const NAV = [
  { to: '/superadmin', label: 'Dashboard', icon: LayoutDashboard, perm: 'super.dashboard.view', end: true },
  { to: '/superadmin/companies', label: 'Companies', icon: Building2, perm: 'super.companies.view' },
  { to: '/superadmin/onboarding', label: 'New Tenant', icon: Rocket, perm: 'super.companies.edit' },
  { to: '/superadmin/plans', label: 'Plans', icon: Package, perm: 'super.billing.view' },
  { to: '/superadmin/billing', label: 'Billing', icon: CreditCard, perm: 'super.billing.view' },
  { to: '/superadmin/stripe-settings', label: 'Stripe Keys', icon: KeyRound, perm: 'super.billing.manage' },
  { to: '/superadmin/staff', label: 'Staff', icon: Users, perm: 'super.staff.view' },
  { to: '/superadmin/roles', label: 'Roles', icon: UsersRound, perm: 'super.roles.view' },
  { to: '/superadmin/platform-email', label: 'Platform Email', icon: Mail, perm: 'super.email.view' },
  // Platform-scoped Domain Auth — uses an isolated company_id sentinel
  // ("__platform__") so opswarex.com SES records never co-mingle with
  // any real tenant's email_domains rows.
  { to: '/superadmin/domain-auth', label: 'Domain Auth', icon: ShieldCheck, perm: 'super.email.view' },
  { to: '/superadmin/broadcast', label: 'Broadcast', icon: Megaphone, perm: 'super.email.broadcast' },
  { to: '/superadmin/feature-flags', label: 'Feature Flags', icon: Flag, perm: 'super.feature_flags.view' },
  { to: '/superadmin/api-quotas', label: 'API Quotas', icon: Gauge, perm: 'super.system.view' },
  { to: '/superadmin/audit-log', label: 'Audit Log', icon: ScrollText, perm: 'super.audit.view' },
  { to: '/superadmin/system-health', label: 'System Health', icon: Activity, perm: 'super.system.view' },
  { to: '/superadmin/data-cleanup', label: 'Data Cleanup', icon: Wrench, perm: 'super.system.view' },
  { to: '/superadmin/sales-channels', label: 'Sales Channels', icon: Package, perm: 'super.system.view' },
];

export const SuperAdminLayout = () => {
  const { user, loading, hasPerm, logout } = useSuperAdminAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-sm text-slate-400">Loading super-admin portal…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/superadmin/login" replace />;
  }

  const visibleNav = NAV.filter((n) => hasPerm(n.perm));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex" data-testid="super-admin-layout">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-800 bg-slate-900 flex flex-col">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-sm font-semibold text-white">Opswarex</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-400">Super Admin</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`
              }
              data-testid={`super-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm font-medium text-white truncate">{user.full_name || user.email}</div>
            <div className="text-xs text-amber-400 truncate">{user.super_role_name}</div>
          </div>
          <button
            onClick={() => {
              logout();
              navigate('/superadmin/login');
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="super-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default SuperAdminLayout;
